import { Box, Stack } from '@mui/material';
import { ContractPageData, ContractPageVersion, GetContractVersionResponse } from '@vy/api-definition';
import type { PerUnitProduct } from '@vy/services';
import { buildCurrencyLevel } from '@vy/services';
import { type CatalogProducts, type FixedProduct, Plan, type UsageProduct } from '@vy/types';
import { isNotEmptyArray, isNotNullOrUndefined, isNullOrUndefined, Nullable, withDefault } from '@vy/utils';
import { FC, useCallback, useMemo, useState } from 'react';
import { VyLoader } from '../../../../../components/new-design/feedback/Loader/VyLoader';
import { useContractVersion } from '../../../../../hooks/requests/use-contract-version';
import { useAnrokEnabled } from '../../../../../hooks/requests/integrations/anrok/use-anrok-enabled';
import { useConnactEnabled } from '../../../../../hooks/requests/integrations/marketplace/use-connact-enabled';
import { useCatalogProductsList } from '../../../../../hooks/requests/use-catalog-products-list';
import { EditProductAnrokSettingsDialog } from '../../ContractFixedFees/components/EditProductAnrokSettingsDialog';
import { EditProductErpSettingsDialog } from '../../ContractFixedFees/components/EditProductErpSettingsDialog';
import { ContractFixedFees } from '../../ContractFixedFees/ContractFixedFees';
import { ContractUsageProductsSection } from '../../ContractUsageProducts/ContractUsageProductsSection';
import { getContractVersions } from '../utils/contract-version-utils';
import { ContractPhaseTimelineSection } from './ContractPhaseTimelineSection';

type ProductProvisionSectionProps = {
  contractPageData: ContractPageData;
  isVersioned: boolean;
  versionIdFromUrl?: Nullable<string>;
  onAddPhase: () => void;
};

export const ProductProvisionSection: FC<ProductProvisionSectionProps> = ({
  contractPageData,
  isVersioned,
  versionIdFromUrl,
  onAddPhase,
}) => {
  const { contract } = contractPageData;
  const contractVersions = useMemo(() => getContractVersions(contractPageData), [contractPageData]);
  const isAnrokEnabled = useAnrokEnabled();
  const isConnactEnabled = useConnactEnabled();

  const { data: catalogProductsResponse } = useCatalogProductsList({ suspense: false });
  const catalogProductsById = useMemo<Map<string, CatalogProducts>>(
    () =>
      new Map(
        (catalogProductsResponse?.catalogProducts ?? []).map((catalogProduct) => [
          catalogProduct.id,
          catalogProduct,
        ]),
      ),
    [catalogProductsResponse],
  );

  const [erpDialogState, setErpDialogState] = useState<{
    product: FixedProduct | PerUnitProduct | UsageProduct | null;
    productName: string | null;
  }>({
    product: null,
    productName: null,
  });

  const [anrokDialogState, setAnrokDialogState] = useState<{
    product: FixedProduct | PerUnitProduct | UsageProduct | null;
    productName: string | null;
    catalogAnrokId: string | undefined;
    overrideAnrokId: string | undefined;
  }>({
    product: null,
    productName: null,
    catalogAnrokId: undefined,
    overrideAnrokId: undefined,
  });

  const { data: contractVersionProducts, isLoading: isVersionLoading } = useContractVersion(
    contract.id,
    versionIdFromUrl ?? undefined,
    {
      suspense: false,
    },
  );

  const planWithVersion = getPlanWithVersion(
    contractVersions,
    contract.plan,
    versionIdFromUrl,
    contractVersionProducts,
  );

  const currency = buildCurrencyLevel({
    customerCurrency: contract.customer.settings?.currency,
  });

  const handleOpenErpDialog = useCallback(
    (product: FixedProduct | PerUnitProduct | UsageProduct, productName: string) => {
      setErpDialogState({ product, productName });
    },
    [],
  );

  const handleCloseErpDialog = useCallback(() => {
    setErpDialogState({ product: null, productName: null });
  }, []);

  const handleOpenAnrokDialog = useCallback(
    (
      product: FixedProduct | PerUnitProduct | UsageProduct,
      productName: string,
      payload: { catalogAnrokId?: string; overrideAnrokId?: string },
    ) => {
      setAnrokDialogState({
        product,
        productName,
        catalogAnrokId: payload.catalogAnrokId,
        overrideAnrokId: payload.overrideAnrokId,
      });
    },
    [],
  );

  const handleCloseAnrokDialog = useCallback(() => {
    setAnrokDialogState({
      product: null,
      productName: null,
      catalogAnrokId: undefined,
      overrideAnrokId: undefined,
    });
  }, []);

  return (
    <Stack spacing={2} sx={{
      marginTop: "20px"
    }}>
      {isVersionLoading ? (
        <VyLoader />
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <ContractFixedFees
            plan={planWithVersion}
            currency={currency}
            isAnrokEnabled={isAnrokEnabled}
            isConnactEnabled={isConnactEnabled}
            catalogProductsById={catalogProductsById}
            onEditErp={handleOpenErpDialog}
            onEditAnrok={handleOpenAnrokDialog}
          />
          <ContractUsageProductsSection
            plan={planWithVersion}
            currency={currency}
            isAnrokEnabled={isAnrokEnabled}
            isConnactEnabled={isConnactEnabled}
            catalogProductsById={catalogProductsById}
            onEditErp={handleOpenErpDialog}
            onEditAnrok={handleOpenAnrokDialog}
          />
        </Box>
      )}
      {isNotNullOrUndefined(erpDialogState.product) && (
        <EditProductErpSettingsDialog
          open={isNotNullOrUndefined(erpDialogState.product)}
          onClose={handleCloseErpDialog}
          productName={withDefault(erpDialogState.productName, '')}
          product={erpDialogState.product}
          contractId={contract.id}
          customerId={contract.customer.id}
        />
      )}
      {isNotNullOrUndefined(anrokDialogState.product) && (
        <EditProductAnrokSettingsDialog
          open={isNotNullOrUndefined(anrokDialogState.product)}
          onClose={handleCloseAnrokDialog}
          productName={withDefault(anrokDialogState.productName, '')}
          product={anrokDialogState.product}
          contractId={contract.id}
          catalogAnrokId={anrokDialogState.catalogAnrokId}
          overrideAnrokId={anrokDialogState.overrideAnrokId}
        />
      )}
      {isVersioned && <ContractPhaseTimelineSection contractPageData={contractPageData} />}
    </Stack>
  );
};

function getPlanWithVersion(
  contractVersions: ContractPageVersion[],
  plan: Plan,
  versionIdFromUrl?: Nullable<string>,
  contractVersionProducts?: GetContractVersionResponse,
) {
  const matchingVersion = contractVersions.find(({ revisionId }) => revisionId === versionIdFromUrl);
  const hasVersions = isNotEmptyArray(contractVersions);
  const isOriginalVersion = isNullOrUndefined(versionIdFromUrl) || !hasVersions;

  const originalProducts = plan.products.filter((product) => isNullOrUndefined(product.revisionId));

  return {
    ...plan,
    createdAt: withDefault(matchingVersion?.createdAt, plan.createdAt),
    products: !isOriginalVersion ? withDefault(contractVersionProducts?.products, []) : originalProducts,
  };
}
